import React, { useState, useEffect, useMemo } from 'react';
import './OptOutLogs.scss';
import LoadingIndicator from '@/components/loader';
import * as XLSX from 'xlsx-js-style';
import { getOptoutLogs } from '@/services/contactOptout';
import { format } from 'date-fns';
import { REPORT_START_DATE } from '@/shared/constant';


// Interfaces
interface SuppressDunsReportServletResponseArrayElement {
    action: string;
    country: string;
    duns: string;
    entity: string;
    industry: string;
    reqtype: string;
    revenue: number;
    submdt: string;
}

type SuppressDunsReportServletResponse = SuppressDunsReportServletResponseArrayElement[];

interface SuppressDunsReportServletResponseArrayElementWithSubmdtDate extends SuppressDunsReportServletResponseArrayElement {
    submdtDate: Date;
}

// Helper function
const groupArrayOfObjects = (list: SuppressDunsReportServletResponseArrayElement[], key: keyof SuppressDunsReportServletResponseArrayElement) =>
    list.reduce((previousValue: Record<number | string, SuppressDunsReportServletResponseArrayElement[]>, currentValue) => {
        const currentValueKeyValue = currentValue[key];
        const current = previousValue[currentValueKeyValue];
        let toUse: SuppressDunsReportServletResponseArrayElement[];
        if (current === undefined) {
            toUse = [];
            previousValue[currentValueKeyValue] = toUse;
        } else {
            toUse = current;
        }
        toUse.push(currentValue);
        return previousValue;
    }, {});


const OptOutLogs = () => {
    const [loading, setLoading] = useState(true);
    const [logData, setLogData] = useState<SuppressDunsReportServletResponse>([]);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await getOptoutLogs();
                setLogData(response.data);
            } catch (error) {
                console.error("Error fetching opt out logs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    const processedData = useMemo(() => {
        if (logData.length === 0) {
            return null;
        }

        const jsonArray: SuppressDunsReportServletResponseArrayElementWithSubmdtDate[] = logData.map((va) => ({ ...va, submdtDate: new Date(va.submdt) }));
        const currentDate = new Date();
        const optDate = REPORT_START_DATE;

        // Year to date and month to date
        const yearToDate = jsonArray.filter((n) => n.action === "Added" && n.submdtDate > new Date(currentDate.getFullYear(), 0, 1)).length;
        const monthToDate = jsonArray.filter((n) => n.action === "Added" && n.submdtDate >= new Date(currentDate.getFullYear(), currentDate.getMonth())).length;

        // Monthly report
        const monthlyReport: { month: string, count: number }[] = [];
        let newDt = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
        while (newDt >= optDate) {
            const startDt = new Date(newDt.getFullYear(), newDt.getMonth());
            const endDt = new Date(startDt.getFullYear(), startDt.getMonth() + 1, 0, 23, 59, 59, 999);
            const count = jsonArray.filter((n) => n.action === "Added" && n.submdtDate >= startDt && endDt >= n.submdtDate).length;
            monthlyReport.push({ month: format(startDt, "MMMM yyyy"), count });
            newDt = new Date(newDt.getFullYear(), newDt.getMonth() - 1);
        }

        // Weekly report
        const weeklyReport: { period: string, count: number }[] = [];
        const copiedDate = new Date(currentDate.getTime());
        copiedDate.setHours(0, 0, 0, 0);
        const daysToSunDay = currentDate.getDay();
        let newSunday = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - daysToSunDay);
        const weekToDateCount = jsonArray.filter((n) => n.action === "Added" && n.submdtDate > newSunday && copiedDate >= n.submdtDate).length;
        weeklyReport.push({ period: `${format(new Date(newSunday.getFullYear(), newSunday.getMonth(), newSunday.getDate() + 1), "MMM dd yyyy")} - present`, count: weekToDateCount });

        let newMonday = new Date(newSunday.getFullYear(), newSunday.getMonth(), newSunday.getDate() - 6);
        while (newSunday >= optDate) {
            const count = jsonArray.filter((n) => n.action === "Added" && n.submdtDate >= newMonday && newSunday >= n.submdtDate).length;
            weeklyReport.push({ period: `${format(newMonday, "MMM dd yyyy")} - ${format(newSunday, "MMM dd yyyy")}`, count });
            newMonday = new Date(newMonday.getFullYear(), newMonday.getMonth(), newMonday.getDate() - 7);
            newSunday = new Date(newSunday.getFullYear(), newSunday.getMonth(), newSunday.getDate() - 7);
        }

        // Revenue report
        const revenueReport = [
            { range: "Over 1 billion", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 1_000_000_000).length },
            { range: "500 million - 1 billion", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 500_000_000 && n.revenue < 1_000_000_000).length },
            { range: "250 million - 500 million", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 250_000_000 && n.revenue < 500_000_000).length },
            { range: "100 million - 250 million", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 100_000_000 && n.revenue < 250_000_000).length },
            { range: "50 million - 100 million", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 50_000_000 && n.revenue < 100_000_000).length },
            { range: "10 million - 50 million", count: jsonArray.filter((n) => n.action === "Added" && n.revenue > 10_000_000 && n.revenue < 50_000_000).length },
            { range: "Under 10 million", count: jsonArray.filter((n) => n.action === "Added" && n.revenue < 10_000_000).length },
        ];

        const newJsonArray = jsonArray.filter((n) => n.action === "Added");

        // Country report
        const countryGroup = groupArrayOfObjects(newJsonArray, "country");
        const countryReport = Object.entries(countryGroup)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .sort((a, b) => b[1].length - a[1].length)
            .map(([country, array]) => ({ country, count: array.length }));

        // Industry report
        const industryGroup = groupArrayOfObjects(newJsonArray, "industry");
        const industryReport = Object.keys(industryGroup).map(industry => ({ industry, count: industryGroup[industry]!.length }));

        // Entity report
        const entityGroup = groupArrayOfObjects(newJsonArray, "entity");
        const entityReport = Object.keys(entityGroup).map(entity => ({ entity, count: entityGroup[entity]!.length }));

        // Request type report
        const reqtypeGroup = groupArrayOfObjects(newJsonArray, "reqtype");
        const reqtypeReport = Object.keys(reqtypeGroup).map(reqtype => ({ reqtype, count: reqtypeGroup[reqtype]!.length }));

        // Actions report
        const actionGroup = groupArrayOfObjects(jsonArray, "action");
        const actionReport = Object.keys(actionGroup).map(action => ({ action, count: actionGroup[action]!.length }));
        const delistedCount = jsonArray.filter((n) => n.action === "Delisted-Removed").length;
        const expiredCount = jsonArray.filter((n) => n.action === "Expired-Removed").length;


        return {
            yearToDate,
            monthToDate,
            monthlyReport,
            weeklyReport,
            revenueReport,
            countryReport,
            industryReport,
            entityReport,
            reqtypeReport,
            actionReport,
            delistedCount,
            expiredCount
        };

    }, [logData]);

    const exportToXLS = () => {
        const table = document.getElementById('opt-out-logs-table');
        if (table) {
            const rows = table.getElementsByTagName('tr');
            const data = [];

            for (let i = 0; i < rows.length; i++) {
                const row = [];
                const cells = rows[i].querySelectorAll('td, th');
                for (let j = 0; j < cells.length; j++) {
                    const cell = cells[j];
                    row.push(cell.innerText);
                }
                data.push(row);
            }

            const ws = XLSX.utils.aoa_to_sheet(data);

            // Set column widths
            ws['!cols'] = [
                { wch: 40 },
                { wch: 60 },
                { wch: 40 },
            ];

            for (let i = 0; i < rows.length; i++) {
                const cells = rows[i].querySelectorAll('td, th');
                for (let j = 0; j < cells.length; j++) {
                    const cell = cells[j];
                    if (cell.getElementsByTagName('b').length > 0) {
                        const address = XLSX.utils.encode_cell({ r: i, c: j });
                        if (ws[address]) {
                            ws[address].s = { font: { bold: true } };
                        }
                    }
                }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Suppression Report');

            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const fileName = `BusinessDirectorySuppressionReport-${year}${month}${day}.xlsx`;
            XLSX.writeFile(wb, fileName);
        }
    };

    return (
        <div className='opt-out-logs-container'>
            <LoadingIndicator inProgress={loading} />
            {!loading && processedData && (
                <>
                    <h3 className="opt-out-logs-header">Suppression report</h3>
                    <button className="opt-out-logs-export-button" type="button" onClick={exportToXLS}>Export to Excel</button>
                    <table id="opt-out-logs-table" className="opt-out-logs-table">
                        <thead>
                            <tr>
                                <th><b>Report</b></th>
                                <th><b>Criteria</b></th>
                                <th><b>Number of suppression requests</b></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td><b>Year to date and month to date report</b></td><td></td><td></td></tr>
                            <tr><td></td><td>Year to date</td><td>{processedData.yearToDate}</td></tr>
                            <tr><td></td><td>Month to date</td><td>{processedData.monthToDate}</td></tr>

                            <tr><td><b>Monthly report</b></td><td></td><td></td></tr>
                            {processedData.monthlyReport.map((item, index) => (
                                <tr key={`monthly-${index}`}><td></td><td>{item.month}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Weekly report</b></td><td></td><td></td></tr>
                            {processedData.weeklyReport.map((item, index) => (
                                <tr key={`weekly-${index}`}><td></td><td>{item.period}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Revenue report</b></td><td></td><td></td></tr>
                            {processedData.revenueReport.map((item, index) => (
                                <tr key={`revenue-${index}`}><td></td><td>{item.range}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Country report</b></td><td></td><td></td></tr>
                            {processedData.countryReport.map((item, index) => (
                                <tr key={`country-${index}`}><td></td><td>{item.country}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Industry report</b></td><td></td><td></td></tr>
                            {processedData.industryReport.map((item, index) => (
                                <tr key={`industry-${index}`}><td></td><td>{item.industry}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Business entity</b></td><td></td><td></td></tr>
                            {processedData.entityReport.map((item, index) => (
                                <tr key={`entity-${index}`}><td></td><td>{item.entity}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Suppression request type</b></td><td></td><td></td></tr>
                            {processedData.reqtypeReport.map((item, index) => (
                                <tr key={`reqtype-${index}`}><td></td><td>{item.reqtype}</td><td>{item.count}</td></tr>
                            ))}

                            <tr><td><b>Actions delisted/expired</b></td><td></td><td></td></tr>
                            {processedData.actionReport.map((item, index) => (
                                <tr key={`action-${index}`}><td></td><td>{item.action}</td><td>{item.count}</td></tr>
                            ))}
                            <tr><td></td><td>Number delisted</td><td>{processedData.delistedCount}</td></tr>
                            <tr><td></td><td>Expired removed</td><td>{processedData.expiredCount}</td></tr>
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default OptOutLogs;
